// utils/blogConfig.ts - With IST time control and weekday filtering
import { BlogPost } from '../types/blog';

const isWeekday = (date: Date): boolean => {
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday = 1, Friday = 5
};

const isAfterPublishTime = (date: Date): boolean => {
  // Convert current time to IST
  const istTime = new Date(date.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
  const publishTime = new Date(istTime);
  publishTime.setHours(8, 14, 0, 0); // 8:14 AM IST
  
  return istTime >= publishTime;
};

const shouldPublishPost = (postDate: Date): boolean => {
  const now = new Date();
  const nowIST = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
  
  // If post date is in the future, don't publish
  if (postDate > nowIST) {
    return false;
  }
  
  // If post date is not a weekday, don't publish
  if (!isWeekday(postDate)) {
    return false;
  }
  
  // If post date is today, check if it's after 8:14 AM IST
  if (postDate.toDateString() === nowIST.toDateString()) {
    return isAfterPublishTime(now);
  }
  
  // If post date is in the past and was a weekday, publish it
  return true;
};

const generateDailyPosts = (): BlogPost[] => {
  const posts: BlogPost[] = [];
  const startDate = new Date('2025-06-06'); // Your first blog post date
  const today = new Date();
  const todayIST = new Date(today.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
  
  // Generate posts for each weekday from start date to today
  for (let date = new Date(startDate); date <= todayIST; date.setDate(date.getDate() + 1)) {
    // Skip weekends
    if (!isWeekday(date)) {
      continue;
    }
    
    // Check if we should publish this post
    if (!shouldPublishPost(new Date(date))) {
      continue;
    }
    
    const dateStr = date.toISOString().split('T')[0]; // Format: 2025-06-09
    
    posts.push({
      id: `nifty-today-${dateStr}`,
      title: `Share-Market-Analysis-${date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`,
      date: dateStr,
      s3Url: `https://wealthmanagerblogs.s3.ap-south-1.amazonaws.com/market-open/${dateStr}.html`,
      category: 'market news',
      excerpt: 'Daily market analysis and insights',
      tags: ['market', 'analysis', 'daily']
    });
  }
  
  return posts.reverse(); // Newest first
};

export const blogPosts = generateDailyPosts();

// Keep your existing helper functions
export const getBlogPostById = (id: string): BlogPost | undefined => {
  return blogPosts.find(post => post.id === id);
};

// Helper function to check when next post will be published (useful for debugging)
export const getNextPublishTime = (): string => {
  const now = new Date();
  const nowIST = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Kolkata"}));
  
  // If today is a weekday and before 8:14 AM IST
  if (isWeekday(nowIST) && !isAfterPublishTime(now)) {
    const today814 = new Date(nowIST);
    today814.setHours(8, 14, 0, 0);
    return `Today at ${today814.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}`;
  }
  
  // Find next weekday
  let nextDay = new Date(nowIST);
  do {
    nextDay.setDate(nextDay.getDate() + 1);
  } while (!isWeekday(nextDay));
  
  nextDay.setHours(8, 14, 0, 0);
  return `${nextDay.toLocaleDateString('en-IN')} at ${nextDay.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}`;
};